const HOT_PAYLOAD_LIMITS_BYTES = Object.freeze({
  pointer: 1024,
  "view-changing": 1024,
  "hover-changed": 2048,
  "view-changed": 8192,
  "hotspot-visibility-changed": 256,
  "layer-progress": 16384,
});

const FORBIDDEN_HOT_PAYLOAD_KEYS = new Set([
  "catalog",
  "currentState",
  "fullState",
  "state",
  "layerData",
  "layers",
  "fish",
  "items",
]);

export function estimateJsonByteLength(value) {
  const json = JSON.stringify(value ?? null);
  return typeof TextEncoder === "function" ? new TextEncoder().encode(json).byteLength : json.length;
}

export function summarizeBridgeEventBatch(payload) {
  const events = Array.isArray(payload?.events) ? payload.events : [];
  if (!events.length) {
    return { eventCount: 0, eventTypes: [], eventCounts: {} };
  }
  const eventTypes = [];
  const eventCounts = {};
  const snapshotKinds = [];
  for (const event of events) {
    const type = String(event?.type || "");
    if (!type) {
      continue;
    }
    eventTypes.push(type);
    eventCounts[type] = (eventCounts[type] || 0) + 1;
    if (type === "snapshot-ready" && event?.kind) {
      snapshotKinds.push(String(event.kind));
    }
  }
  return {
    eventCount: eventTypes.length,
    eventTypes,
    eventCounts,
    ...(snapshotKinds.length ? { snapshotKinds } : {}),
    ...(Number.isFinite(Number(payload?.sequence)) ? { sequence: Number(payload.sequence) } : {}),
  };
}

function payloadType(value) {
  return String(value?.type || value?.kind || "").trim();
}

function collectForbiddenPaths(value, path = "$", paths = []) {
  if (!value || typeof value !== "object") {
    return paths;
  }
  if (Array.isArray(value)) {
    const limit = Math.min(value.length, 16);
    for (let index = 0; index < limit; index += 1) {
      collectForbiddenPaths(value[index], `${path}[${index}]`, paths);
    }
    return paths;
  }
  for (const [key, child] of Object.entries(value)) {
    const childPath = `${path}.${key}`;
    if (FORBIDDEN_HOT_PAYLOAD_KEYS.has(key)) {
      paths.push(childPath);
    } else {
      collectForbiddenPaths(child, childPath, paths);
    }
  }
  return paths;
}

export function validateFishyMapHotEventPayload(event) {
  const type = payloadType(event);
  const estimatedBytes = estimateJsonByteLength(event);
  const limitBytes = HOT_PAYLOAD_LIMITS_BYTES[type] || 0;
  if (!limitBytes) {
    return { ok: true, type, estimatedBytes, limitBytes: 0, forbiddenPaths: [] };
  }

  const forbiddenPaths = collectForbiddenPaths(event);
  const violations = [];
  if (estimatedBytes > limitBytes) {
    violations.push(`payload ${estimatedBytes} B exceeds ${limitBytes} B`);
  }
  if (forbiddenPaths.length) {
    violations.push(`forbidden hot branches: ${forbiddenPaths.join(", ")}`);
  }
  return {
    ok: violations.length === 0,
    type,
    estimatedBytes,
    limitBytes,
    forbiddenPaths,
    violations,
  };
}

export function assertFishyMapHotEventPayload(event) {
  const result = validateFishyMapHotEventPayload(event);
  if (!result.ok) {
    const error = new Error(
      `invalid fishymap hot payload (${result.type}): ${result.violations.join("; ")}`,
    );
    error.name = "FishyMapHotPayloadError";
    error.details = result;
    throw error;
  }
  return event;
}
